// English translations. Структура должна совпадать с `ru.ts` —
// TypeScript ловит расхождения через тип `typeof ru`.
import type ru from './ru'

const en: typeof ru = {
  common: {
    languageRu: 'Русский',
    languageEn: 'English',
    loading: 'Loading…',
    cancel: 'Cancel',
    close: 'Close',
    save: 'Save',
    copy: 'Copy',
    copied: 'Copied',
    email: 'Email',
    phone: 'Phone',
  },
  toast: {
    errorTitle: 'Error',
    details: 'Details',
    hide: 'Hide',
    statusCode: 'Status',
  },
  shell: {
    tabs: {
      monitoring: 'Monitoring',
      planning: 'Planning',
      configuration: 'Configuration',
      system: 'System',
    },
    noLocations: 'No locations assigned',
  },
  sidebar: {
    expand: 'Expand',
    collapse: 'Collapse',
    monitoring: {
      processing: 'Operations',
      transport: 'Transport',
      cargo: 'Cargo',
      stats: 'Statistics',
      messagesQueue: 'Messages queue',
    },
    planning: {
      timeslots: 'Time slots',
      shipping: 'Shipping schedule',
      supply: 'Supply schedule',
    },
    configuration: {
      myData: 'My data',
      composition: 'Composition',
      queues: 'Queues',
      directions: 'Directions',
    },
    system: {
      objects: 'Objects',
      counterparties: 'Counterparties',
      users: 'Users',
      integrations: 'Integrations',
      administration: 'Administration',
    },
  },
  profileMenu: {
    aria: 'Profile menu',
    fallbackTitle: 'Profile',
    profile: 'Profile',
    accountManagement: 'Account management',
    contactSupport: 'Contact support',
    logout: 'Sign out',
    roles: {
      account_owner: 'Account owner',
      company_admin: 'Administrator',
      configurator: 'Configurator',
      planner: 'Planner',
      operator: 'Operator',
      counterparty: 'Counterparty',
    },
    support: {
      title: 'Contact support',
      description:
        'Describe what happened and attach a screenshot if possible. We reply during business hours (Moscow, 9:00–19:00).',
      formHint:
        'An in-app contact form is coming later — for now please write or call us directly.',
    },
  },
  locationDropdown: {
    selectLocation: 'Select location',
    noLocations: 'No locations assigned',
  },
  sectionShell: {
    accessDenied: '🔒 Access denied',
    pickLocation: '📍 Select location',
  },
  profilePage: {
    languageSection: 'Interface language',
    languageHint:
      'Choice is saved in this browser only. Profile-side sync will come in the next step.',
    workspaceSection: 'Workspace',
    showOnlyGrantedLabel: 'Only locations with explicit access',
    showOnlyGrantedHint: 'As an account owner you implicitly see all locations of all organizations in the account. Enable this to keep only those you were explicitly granted access to (like a regular operator). Useful when the account has many locations but you work with a small subset day-to-day.',
  },
  roles: {
    ACCOUNT_OWNER: 'Owner',
    COMPANY_ADMIN: 'Org admin',
    CONFIGURATOR: 'Site admin',
    PLANNER: 'Coordinator',
    OPERATOR: 'Operator',
    COUNTERPARTY: 'Partner',
  },
  loginPage: {
    brandTitle: 'RITM-FLOW',
    brandTagline: 'Logistics management system',
    brandHint: 'Manage shipments, schedules, and fleet from a single dashboard.',
    formTitle: 'Sign in',
    formSubtitle: 'Enter your username and password to access your workspace',
    loginPlaceholder: 'Username',
    passwordPlaceholder: 'Password',
    loginButton: 'Sign in',
    loginButtonLoading: 'Signing in…',
    errFill: 'Please fill in all fields',
    errLogin: 'Sign-in error',
    errUserInfo: 'Failed to load user data',
    errWrongCreds: 'Invalid username or password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    footer: '© {year} Ritmflow',
    forgotLink: 'Forgot password?',
    resetTitle: 'Reset password',
    resetSubtitle:
      'Enter your email or phone. If such an account exists, a reset link will be sent to it.',
    resetPlaceholder: 'Email or phone',
    resetSubmit: 'Send link',
    resetSubmitLoading: 'Sending…',
    resetBack: 'Back to sign in',
    resetNeutralOk:
      'If such an account exists, a password reset link will be sent.',
  },
  onboardingPage: {
    title: 'Account setup',
    welcomeOwner: 'To start using the system, create your first organization and its location. A location is a place where cargo moves between (warehouse, office, shop). Address and coordinates can be filled in later.',
    welcomeNotOwner: 'There are no locations in the account yet. Wait until the account owner finishes the setup — after that you’ll be able to use the Monitoring, Planning and other sections.',
    noAccessTitle: 'You don’t have access to any location',
    noAccessHint: 'Ask the account administrator to grant you access to at least one location.',
    ctaCreate: 'Create organization and location',
    accountInfo: 'Account: {name}',
  },
  accountPage: {
    accessDeniedTitle: '🔒 Account owner access only',
    accessDeniedHint: 'This section is available only to users with the "Account owner" role.',
    accountEyebrow: 'Account',
    renameTooltip: 'Rename account',
    planBadge: 'plan: {plan}',
    createdAt: 'created {date}',
    ownerLabel: 'Owner',
    transferOwnerLink: 'Transfer to another user',
    accountSummaryLabel: 'Account contents',
    statsCompanies: 'Orgs:',
    statsLocations: 'Locations:',
    statsUsers: 'Users:',
    statsCounterparties: 'Counterparties:',
    holdingCompanies: 'Holding organizations',
    addCompanyShort: 'Add',
    noCompaniesHint: 'No organizations yet. Add one — it will automatically become the primary.',
    primaryBadge: 'primary',
    status: {
      trial: 'Trial',
      active: 'Active',
      suspended: 'Suspended',
      closed: 'Closed',
    },
    rename: {
      title: 'Rename account',
      nameLabel: 'New name',
      saving: 'Saving…',
      errEmpty: 'Name cannot be empty.',
      errSave: 'Could not save changes',
    },
    addCompany: {
      title: 'Add organization',
      saving: 'Creating…',
      submit: 'Create',
      errEmpty: 'Short name is required.',
      errEmptyLocation: 'First location name is required.',
      errCreate: 'Could not create organization',
      makePrimary: 'Make primary',
      primaryWarning: '· the current primary will no longer be primary',
      locationSectionTitle: 'First location',
      locationSectionHint: 'An organization must have at least one location — that’s where cargo moves between. Address and coordinates can be filled in later.',
      fields: {
        displayName: 'Short name',
        displayNamePlaceholder: 'Alfa Logistics',
        country: 'Country',
        countryPlaceholder: 'RU',
        inn: 'INN',
        innPlaceholder: '7707…',
        kpp: 'KPP',
        ogrn: 'OGRN',
        legalAddress: 'Legal address',
        contactPhone: 'Contact phone',
        contactPhonePlaceholder: '+7 …',
        locationName: 'Location name',
        locationNamePlaceholder: 'Warehouse "Moscow-1"',
        locationKind: 'Location kind',
        isOperational: 'Operational location',
        isOperationalHint: 'Our operators work here and the terminal is installed — will appear in the header.',
        isPassiveHint: 'Passive address-only point for cargo flow — cannot be picked in the header.',
        firstOperationalRequired: 'The first location of the account must be operational.',
      },
      kindOptions: {
        OFFICE: 'Office',
        WAREHOUSE: 'Warehouse',
        SHOP: 'Shop',
        PARKING: 'Parking',
        LOGISTICS_CENTER: 'Logistics center',
        SHOPPING_COMPLEX: 'Shopping complex',
        CAR_FLEET: 'Car fleet',
        LOGISTICS_COMPLEX: 'Logistics complex',
      },
    },
    transfer: {
      title: 'Transfer account ownership',
      description:
        'After the transfer you will no longer be the owner — your role will change to "Org admin". You will keep access to user and integration management, but only the new owner will be able to transfer or close the account.',
      noCandidates:
        'There are no other active users in the account to transfer ownership to. Invite someone first via "System → Users".',
      newOwnerLabel: 'New owner',
      pickUserOption: '— pick a user —',
      passwordLabel: 'Confirm with password',
      passwordPlaceholder: 'Your current password',
      submit: 'Transfer ownership',
      submitting: 'Transferring…',
      confirm: 'Transfer account ownership? This action cannot be undone.',
      errNoTarget: 'Select a new owner.',
      errNoPassword: 'Confirm with your password.',
      errFail: 'Could not transfer ownership',
    },
    modal: {
      closeAria: 'Close',
    },
  },
  queues: {
    addQueue: 'Add Queue',
    createTitle: 'New Queue',
    deleteConfirm: 'Delete queue «{{name}}»?',
    deleteHint: 'All schedules and timeslots for this queue will also be deleted.',
    serviceModeLabel: 'Service Mode',
    serviceMode: {
      always_first: 'Always First',
      cyclic: 'Cyclic Order',
      random: 'Random',
      operator: 'Operator Controlled',
    },
    queueType: 'Queue Type',
    queueTypeOptions: {
      service: 'Service',
      parking: 'Parking',
      control: 'Inspection',
    },
    queueColor: 'Color',
    queueName: 'Name',
    queueNamePlaceholder: 'E.g. Loading',
    cargoModesLabel: 'Served cargo types',
    cargoModesHint: 'If nothing is selected, the queue handles any cargo type.',
    cargoModesEmpty: 'Cargo type directory is empty.',
    cargoModesAny: 'Any cargo type',
    pickColorManually: 'Pick color manually',
    createButton: 'Create',
    nameRequired: 'Name is required',
    createError: 'Failed to create queue',
    selectQueueHint: 'Pick a queue to view its schedule',
    edit: 'Edit',
    delete: 'Delete',
    planDayIntervals: {
      title: 'Plan intervals',
      save: 'Plan',
      startTime: 'Day start time',
      endTime: 'Day end time',
      intervalLabel: 'Interval size',
      intervalOptions: {
        m30: '30 minutes',
        m60: '1 hour',
        m90: '1 hour 30 minutes',
        m120: '2 hours',
        m180: '3 hours',
        m240: '4 hours',
        m360: '6 hours',
        m480: '8 hours',
      },
      lunchToggle: 'Lunch break',
      lunchStart: 'Lunch start',
      lunchEnd: 'Lunch end',
      serviceSectionTitle: 'Service parameters (in minutes)',
      maneuverTime: 'Maneuver time',
      serviceTime: 'Service time',
      activeDocks: 'Active docks',
      activeDocksHint: 'Default — sites attached to the queue ({n}).',
      throughput: 'Throughput (rides)',
      throughputAutoBtn: 'auto',
      throughputAutoBtnTitle: 'Restore auto-calc',
      throughputAutoHint: 'Auto = ⌊{docks} × {minutes} min ÷ ({maneuver} + {service}) min⌋ = {result}.',
    },
  },
  /* __I18N_ANCHOR_ACCOUNT_PAGE__ */
  notifications: {
    title: 'Notifications',
    empty: 'All quiet',
    emptyHint: 'When something needs your attention, it will show up here.',
    markAllRead: 'Mark all read',
    showResolved: 'Show resolved',
    hideResolved: 'Hide resolved',
    resolvedBadge: 'Resolved',
    openSettings: 'Notification settings',
    bellAria: 'Notifications',
    scope: {
      active: 'This location',
      all: 'All mine',
    },
    connection: {
      online: 'Connected',
      offline: 'Disconnected · polling',
    },
    footer: {
      shown: 'Showing {n}',
    },
    types: {
      terminal_pending: {
        label: 'New terminal waiting for approval',
        title: 'New terminal waiting for approval',
        subtitle: '{terminal_label} · {location_name}',
      },
      driver_message_incoming: {
        label: 'Incoming driver messages',
        title: 'Driver message',
        subtitle: '{driver_name} · {message_type_name}',
      },
      driver_registration: {
        label: 'Driver registration at terminal',
        title: 'Driver registration',
        subtitle: 'Vehicle {vehicle_number}',
      },
    },
    actions: {
      approve: 'Approve',
      reject: 'Reject',
      resolve: 'Done',
      reply: 'Reply',
      open: 'Open',
      formCruise: 'Form a cruise',
    },
    rejectReasonPrompt: 'Rejection reason:',
    errors: {
      approveFail: 'Failed to approve',
      rejectFail: 'Failed to reject',
      resolveFail: 'Failed to resolve',
    },
    detail: {
      message: 'Message',
      data: 'Data',
      noData: 'No additional data.',
    },
    severity: {
      INFO: 'Info',
      WARN: 'Warning',
      URGENT: 'Urgent',
    },
    fields: {
      terminal_label: 'Terminal',
      device_type_name: 'Device type',
      location_name: 'Location',
      message_type_name: 'Message type',
      driver_name: 'Driver',
      vehicle_number: 'Vehicle plate',
      phone: 'Phone',
      counterparty_name: 'Counterparty',
      operation_type_name: 'Operation',
      cargo_mode_name: 'Cargo type',
      unit_name: 'Unit',
      amount: 'Amount',
    },
    settings: {
      title: 'Notifications',
      hint: 'Enable categories you want to receive in the bell.',
    },
  },
  /* __I18N_ANCHOR_PROFILE_PAGE_EXT__ */
  /* __I18N_ANCHOR_LANDINGS__ */
  /* __I18N_ANCHOR_PLACEHOLDERS__ */
  /* __I18N_ANCHOR_MODALS__ */
  /* __I18N_ANCHOR_SECTION_HINTS__ */
  shared: {
    locationKind: {
      OFFICE: 'Office',
      WAREHOUSE: 'Warehouse',
      SHOP: 'Shop',
      PARKING: 'Parking',
      LOGISTICS_CENTER: 'Logistics center',
      SHOPPING_COMPLEX: 'Shopping complex',
      CAR_FLEET: 'Car fleet',
      LOGISTICS_COMPLEX: 'Logistics complex',
    },
    syncTooltip: 'Synced with global record — editing is disabled',
  },
  /* __I18N_ANCHOR_OBJECTS__ */
  objectsPage: {
    loading: 'Loading…',
    noLocations: 'This company has no locations yet.',
    operationalBadge: 'Operational',
    passiveBadge: 'Passive',
    operationalLabel: 'Operational location',
    operationalHint: 'Switched on for operator work: visible in the header, queues and sites are configured here. Disable to turn the location into a passive address-only point — it stays in the catalog for cargo flow but never appears in the header.',
    lastOperationalBlocked: 'This is the last operational location of the account — you cannot disable it. Make another one operational first.',
    bailorZonesTitle: 'Bailors hosted here',
    bailorZonesHint: 'Counterparties on whose behalf we operate at this location: their goods are stored here (3PL). Each zone mirrors the location itself.',
    bailorChip: 'Bailor',
  },
  statsPage: {
    cruisesTitle: 'Cruise statistics',
    timesTitle: 'Time metrics',
    totalCount: 'Total',
    totalAvg: 'Average',
    exportExcel: 'Export to Excel',
    exportError: 'Failed to generate file',
    viewTable: 'Table',
    viewChart: 'Chart',
    allQueues: 'All queues',
    loading: 'Loading statistics…',
    empty: 'No data for the period.',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    prevYear: 'Previous year',
    nextYear: 'Next year',
    currentMonth: 'Current month',
    weekend: 'Weekend',
    period: { day: 'Day', week: 'Week', month: 'Month' },
    periodSuffix: { day: 'for the day', week: 'for the week', month: 'for the month' },
    prevDay: 'Previous day',
    nextDay: 'Next day',
    today: 'Today',
    units: { h: 'h', m: 'm' },
    gateLoadTitle: 'Gate utilization',
    gateLoadEmpty: 'No queues with gates — link gates to queues in Configuration.',
    gatesCount: 'gates: {n}',
    loadTotal: 'For the period',
    loadAvg: 'average',
    clickHint: 'Click a number or a bar to see the cruises',
    colName: 'Name',
    sortHint: 'Sort',
    lateToleranceNote: 'Counted as late only when more than {n} min past the plan',
    details: {
      tabCruises: 'Cruises',
      tabHours: 'By hour',
      tabGates: 'Gates',
      loading: 'Loading…',
      error: 'Failed to load details',
      empty: 'No cruises.',
      closed: 'day off',
      truncated: 'Showing the first {shown} of {total}.',
      wholePeriod: 'whole period',
      colCruise: 'Cruise',
      colVehicle: 'Vehicle',
      colGate: 'Gate',
      colQueue: 'Queue',
      colStatus: 'Status',
      colPlan: 'Plan',
      colArrival: 'Arrival',
      colBegin: 'Start',
      colFinish: 'Finish',
      colLoad: 'Utilization',
      colBusy: 'Busy / available',
      colCruises: 'Cruises',
      value: {
        served_late: 'Delay',
        delay: 'Delay',
        late_arrivals: 'Lateness',
        lateness: 'Lateness',
        service: 'Service',
        waiting: 'Waiting',
        placement: 'Placement',
        gate_load: 'Busy',
      },
      hours: { plan: 'Plan', arrival: 'Arrival', begin: 'Service start', finish: 'Finish' },
    },
    rows: {
      on_time: 'Served on time',
      served_late: 'Served late',
      late_arrivals: 'Late arrivals served',
      cancelled: 'Cancelled',
      rescheduled: 'Rescheduled',
      service: 'Avg. service time',
      placement: 'Avg. gate placement time',
      waiting: 'Avg. service waiting time',
      delay: 'Avg. service delay',
      lateness: 'Avg. lateness',
      gate_load: 'Gate utilization',
    },
    count: { delay: 'delayed', lateness: 'late' },
  },
  /* __I18N_ANCHOR_USERS_INTEGRATIONS_ADMIN__ */
  usersPage: {
    search: 'Search by name, email, phone, role…',
    total: 'total:',
    addUser: 'Add user',
    loading: 'Loading…',
    emptyAccount: 'No users in this account yet. Add the first one.',
    emptySearch: 'No one matches the search.',
    colUser: 'User',
    colRole: 'Role',
    colAccess: 'Access',
    blocked: 'blocked',
    fullAccess: 'full access',
    orgs: 'org.',
    locs: 'loc.',
    selectHint: 'Select a user on the left…',
    fieldLastName: 'Last name',
    fieldFirstName: 'First name',
    fieldPatronymic: 'Patronymic',
    fieldEmail: 'Email',
    fieldPhone: 'Phone',
    atLeastOneContact: 'At least one contact must remain: email or phone.',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    block: 'Block',
    blockConfirm: 'Block "{name}"? The user will not be able to sign in to this account. The record is preserved — re-activate via edit.',
    statusActive: 'Active',
    statusActiveLower: 'active',
    accessSection: 'Access to organisations and locations',
    ownerAccessHint: 'Account owner has full access to all companies and locations by default. Specific grants below are for information and audit purposes.',
    noGrantsOwner: 'No explicit grants — the "allow all" from the role applies.',
    noGrantsEmpty: 'No access grants. In view mode the user will not see account data — grant access to at least one organisation.',
    canModify: 'can modify',
    record: 'record',
    ownerFullAccessHint: 'Account owner has access to everything by default — checkboxes restrict the set only in specific cases.',
    addedAt: 'Added',
    registeredAt: 'Registered',
    readOnly: 'read-only',
    readWrite: 'read + write',
    inviteSection: 'Sign-in invitation',
    inviteStatusNone: 'not invited',
    inviteStatusPending: 'link issued, awaiting activation',
    inviteStatusActivated: 'sign-in activated',
    inviteStatusExpired: 'expired',
    issuedAt: 'issued:',
    validUntil: 'valid until',
    accepted: 'accepted',
    copyPwdLinkHint: 'Password reset link issued — copy it now, it cannot be shown again',
    copyInviteLinkHint: 'Invite link issued — copy it now, it cannot be shown again',
    copyLink: 'Copy link',
    issuePwdLink: 'Issue a one-time link the user can use to set a new password',
    issuing: 'Issuing…',
    sendPwdLink: 'Send password reset link',
    issueInviteLink: 'Issue invite link — after setting a password the user can sign in',
    reissueLink: 'Reissue link — the previous one will stop working immediately',
    invite: 'Invite',
    reissue: 'Reissue link',
    modal: {
      title: 'New user',
      close: 'Close',
      hint: 'Email or phone is enough. If such a user already exists on the platform — they will be added to this account.',
      noCompaniesHint: 'This account has no organisations yet — add one first, then grant access to it.',
      primaryBadge: 'primary',
      cancel: 'Cancel',
      create: 'Create',
      errContact: 'Email or phone is required',
      creating: 'Creating…',
    },
  },
  integrationsPage: {
    kindWms: 'WMS — warehouse',
    kindTms: 'TMS — transport',
    kindErp: 'ERP — accounting',
    kindOther: 'Other',
    transportHttpIn: 'HTTP (inbound)',
    transportHttpOut: 'HTTP (outbound)',
    transportFile: 'File exchange',
    transportMixed: 'Mixed',
    entityCargo: 'Cargo',
    entityCruise: 'Trip',
    entityOther: 'Other',
    lastActivityNever: 'never',
    lastActivityNow: 'just now',
    lastActivityHours: '{n} h ago',
    lastActivityDays: '{n} d ago',
    loading: 'Loading…',
    emptyHint: 'No integrations yet. Create the first one — it will get an inbound token the external system can use to send data.',
    addIntegration: 'Add integration',
    selectHint: 'Expand an integration on the left to see the exchange log and upload a file manually.',
    modal: {
      title: 'New integration',
      fieldName: 'Name',
      fieldKind: 'Type',
      fieldTransport: 'Transport',
      fieldDescription: 'Description',
      namePlaceholder: 'WMS Vladivostok',
      descriptionPlaceholder: 'Purpose, what it sends, who administers it on the other side',
      errCreate: 'Could not create integration',
      errName: 'Name is required',
      creating: 'Creating…',
      create: 'Create',
    },
    msgCount: 'messages:',
    lastError: 'last error:',
    createdAt: 'Created',
    lastExchange: 'Last exchange',
    inboundSection: 'Inbound URL & token',
    copyUrl: 'Copy inbound URL',
    copyToken: 'Copy token only',
    tokenHint: 'The token can be passed in the URL OR in the X-Integration-Token header',
    regenConfirm: 'Regenerate token? The old one will immediately stop accepting requests — the external system will need the new one.',
    disable: 'Disable',
    enable: 'Enable',
    disableLabel: 'Disable integration',
    enableLabel: 'Enable integration',
    ingestModeOn: 'Accept data',
    ingestModeOff: 'Log only',
    ingestModeOnLabel: 'Enable parsing of inbound messages into cruises and cargo',
    ingestModeOffLabel: 'Switch to "log only" mode — store messages but do not parse them',
    ingestBadgeOn: 'accepting data',
    ingestBadgeOff: 'log mode',
    deleteConfirm: 'Delete integration "{name}"? All its messages will be deleted too.',
    delete: 'Delete',
    lastErrorSection: 'Last error',
    upload: 'Upload file',
    uploading: 'Uploading…',
    uploaded: 'uploaded:',
    logSection: 'Exchange log · total',
    logReceived: 'received:',
    logErrors: 'errors:',
    logEmpty: 'No messages yet. When an external system sends something to the inbound URL or you upload a file manually — they will appear here.',
    statusReceived: 'received',
    statusProcessed: 'processed',
    statusError: 'error',
    msgTitle: 'Message #{id}',
    receivedAt: 'Received:',
    msgFile: 'file:',
    openOriginal: 'open original',
    tabJson: 'JSON',
    tabText: 'Text',
    tabHeaders: 'Headers',
    empty: '(empty)',
    processingError: 'Processing error:',
    scheduleError: 'Auto-schedule error:',
    viewAllJournal: 'Full log',
    reprocessUnprocessed: 'Reprocess unprocessed',
    reprocessUnprocessedTitle: 'Reprocess received and errored cargos and cruises',
    reprocessAll: 'Reprocess all',
    reprocessAllTitle: 'Reprocess every log record, including already processed ones',
    reprocessAllConfirm: 'Reprocess the whole log, including already processed records? All cargos and cruises will be parsed again.',
    reprocessResult: 'Reprocessed: {processed} of {total}',
    reprocessErrorsSuffix: ', errors: {failed}',
    reprocessSkippedSuffix: ', skipped: {skipped}',
    clearJournal: 'Clear journal',
    clearJournalTitle: 'Permanently delete all exchange journal messages',
    clearJournalConfirm: 'Delete ALL {total} exchange journal messages? This cannot be undone. Cargo and trips already parsed from the messages are kept — only the raw journal is removed.',
    clearing: 'Deleting…',
    clearJournalResult: 'Messages deleted: {deleted}',
    journalTitle: 'Exchange log — {name}',
    journalFilterStatus: 'Status',
    journalFilterDirection: 'Direction',
    journalFilterKind: 'Payload',
    journalFilterAll: 'All',
    directionInbound: 'Inbound',
    directionOutbound: 'Outbound',
    journalShown: 'Showing {n} of {total}',
    journalEmpty: 'No messages match the selected filters.',
    loadMore: 'Load more',
    loadingMore: 'Loading…',
    targetLocation: 'Target location',
    targetLocationHint: 'Where cruises and cargo from this integration are written',
    targetLocationNone: 'Not selected',
    targetLocationRequired: 'Data acceptance is on, but no target location is selected — parsing will fail with a "location_id not set" error. Pick a location.',
    autoGraphSection: 'Auto-create schedules',
    autoGraphHint: 'Create planned schedule points together with incoming trips',
    assignSiteIncomeSection: 'Assign gate — inbound',
    assignSiteIncomeHint: 'Look up the gate by SITE code and bind it to inbound cruise/cargo. If off — no gate is assigned (field stays empty)',
    assignSiteOutgoingSection: 'Assign gate — outbound',
    assignSiteOutgoingHint: 'Look up the gate by SITE code and bind it to outbound cruise/cargo. If off — no gate is assigned (field stays empty)',
    allowGateOverlapSection: 'Allow gate overlap',
    allowGateOverlapHint: 'Multiple cruises on the same gate are processed without the "gate occupied" error. Enable when the source (WMS) allows several cruises to be serviced at one gate at the same time',
    gateCodesSection: 'Gate exchange codes',
    gateCodesHint: 'Code from the SITE field of incoming messages — parsing uses it to find the gate in the target location. Codes are unique within a location',
    gateCodesFilled: '{n} of {m} filled',
    gateCodesEmpty: 'The target location has no gates (site type «Место погрузки»). Add them in "Configuration → Composition"',
    gateCodesPlaceholder: 'Exchange code',
    gateCodesSaveError: 'Could not save the code',
    finishConfirmedBySection: 'Service finish confirmation',
    finishConfirmedByHint: 'Who marks the cruise service as finished. "Location staff" — a warehouse worker (the driver sees "Waiting"). "Driver / expeditor" — marks the finish in their own terminal',
    finishConfirmedByStaff: 'Location staff',
    finishConfirmedByDriver: 'Driver / expeditor',
    reprocess: 'Reprocess',
    reprocessing: 'Processing…',
    payload: {
      readableTab: 'Readable',
      empty: '—',
      fieldDocTitle: 'Name',
      fieldStatus: 'Status',
      fieldDtype: 'Operation',
      fieldSmode: 'Storage mode',
      fieldLocFrom: 'Sender',
      fieldLocTo: 'Receiver',
      fieldDocId: 'Document no.',
      fieldDocDate: 'Document date',
      fieldDocPriority: 'Priority',
      fieldGuid: 'Identifier (GUID)',
      fieldVersion: 'Version',
      fieldSite: 'Site',
      fieldTruck: 'Vehicle',
      fieldPlanTime: 'Planned start',
      fieldStartTime: 'Start',
      fieldEndTime: 'End',
      fieldCargoList: 'Cargos',
      cargoItem: 'Cargo {n}',
      status10: 'In service',
      status11: 'Planned',
      status12: 'Cancelled',
      status16: 'Done',
      statusUnknown: 'Status {code}',
      dtypeIncome: 'Inbound',
      dtypeSale: 'Outbound',
      smodeDry: 'Dry',
      smodeFrozen: 'Frozen',
      smodeChilled: 'Chilled',
    },
  },
  administrationPage: {
    siteTemplates: 'Site templates',
    detailSection: 'Details',
    colName: 'Name',
    colType: 'Type',
    colStatus: 'Status',
    scopeGlobal: '🌍 Global',
    scopeAccount: '🏢 Account',
    descriptionSection: 'Description',
    structureSection: 'Structure',
    selectHint: 'Select a template',
  },
  /* __I18N_ANCHOR_COUNTERPARTIES__ */
  counterpartiesPage: {
    search: 'Search by name, INN, code…',
    filterAll: 'All',
    filterSupplier: 'Suppliers',
    filterReceiver: 'Receivers',
    filterPartner: 'Partners',
    filterBailor: 'Bailors',
    filterOwn: 'Own',
    recentlyChanged: 'Recently changed',
    recentlyChangedHint: 'Show only counterparties changed within the selected window (3 or 7 days) for quick duplicate review',
    days: 'd',
    innDuplicates: 'INN matches:',
    innFilterActive: 'Filter active — showing only counterparties with duplicate INN. Click to clear.',
    innFilterHint: 'Click to show only counterparties with a matching INN and review pairs for merge.',
    loading: 'Loading…',
    emptyCompany: 'This company has no counterparties yet.',
    emptySearch: 'No one matches the current search / filter.',
    showMasterTooltip: 'Show master link (mirror)',
    hideMasterTooltip: 'Hide master link (mirror)',
    colName: 'Name',
    colLocations: 'Locations',
    syncGlobal: 'Global directory data',
    syncLocal: 'Company directory data',
    mirrorOf: 'Mirror of counterparty "{name}"',
    blocked: 'blocked',
    blockedHint: 'Disabled — does not appear in selection dropdowns',
    innDuplicateHint: 'INN {inn} matches: {names}. Possible duplicate — consider linking as "sync with another account counterparty".',
    selectHint: 'Select a counterparty to see its details.',
    detail: {
      fieldDisplayName: 'Short name',
      fieldLegalName: 'Full legal name',
      fieldLastName: 'Last name',
      fieldFirstName: 'First name',
      fieldInn: 'INN',
      fieldCode: 'Internal code',
      fieldNotes: 'Notes',
      locationsSection: 'Locations',
      locationsCount: '({count})',
      noLocations: 'Counterparty has no locations. Cargo moves between Locations — add at least one.',
      createdAt: 'Created',
      updatedAt: 'Updated',
      isBlocked: 'Disabled',
      blockedDescription: 'Counterparty is hidden from selection dropdowns in Cruise/Cargo. Agreement suspended — but the record is preserved for history.',
      isAvailable: 'Available',
      availableHint: 'Uncheck to hide the counterparty from Cruise/Cargo dropdowns without deleting the record.',
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      uploadLogo: 'Upload logo',
      deleteLogo: 'Delete logo',
      loading: 'Loading…',
      master: 'Master',
      errSave: 'Could not save',
    },
    localLocation: {
      backToCounterparty: '← Back to counterparty',
      title: 'Counterparty location',
      synced: 'Synced',
      fieldFullName: 'Full name',
      fieldKind: 'Object type',
      fieldTimezone: 'Timezone',
      fieldAddress: 'Address',
      fieldPhone: 'Contact phone',
      fieldCoords: 'Coordinates',
      fieldId: 'ID',
      fieldCode: 'Internal code',
      fieldDescription: 'Description',
      fieldWorkMode: 'Working hours',
      fieldPhoto: 'Photo',
      fieldDrivingScheme: 'Driving directions',
      kindWarehouse: 'Warehouse',
      kindTerminal: 'Terminal',
      kindDock: 'Dock / loading point',
      kindOffice: 'Office',
      kindOther: 'Other',
      purposeSection: 'Location purpose',
      purposeNone: '— not set —',
      purposeSupplier: 'Supplier',
      purposeReceiver: 'Receiver',
      purposeSite: 'Site',
      defaultTimezone: 'Europe/Moscow',
      noPhoto: 'no photo',
      noDrivingScheme: 'no directions',
      upload: 'Upload',
      replace: 'Replace',
      delete: 'Delete',
      loading: 'Loading…',
      file: 'File',
      workModeEditNotSupported: 'Editing working hours is not supported in this version.',
      createdAt: 'Created',
      updatedAt: 'Updated',
      isBlocked: 'Disabled',
      blockedDescription: 'Location is hidden from selection dropdowns in Cruise/Cargo.',
      isAvailable: 'Available',
      availableHint: 'Uncheck to hide the location from Cruise/Cargo dropdowns without deleting the record.',
      syncGlobal: 'Global directory data',
      syncLocal: 'Company directory data',
      master: 'Master',
      globalLocation: 'Global location',
      workModeGlobal: 'Working hours (global)',
      photoGlobal: 'Photo (global)',
      drivingSchemeGlobal: 'Driving directions (global)',
      unlinkFromLocal: 'Unlink "{display_name}" from location "{name}"? Local values will remain but sync will be disabled.',
      unlinkFromGlobal: 'Unlink "{display_name}" from global location? Local values will remain but sync will be disabled.',
    },
    syncControls: {
      sectionTitle: 'Sync settings',
      save: 'Save',
      cancel: 'Cancel',
      manageAria: 'Manage sync',
      manageTitle: 'Change settings, refresh, or unlink',
      autoSyncLabel: 'Auto-update from global',
      autoSyncOn: 'on',
      autoSyncOff: 'off',
      syncedFields: 'Synced fields:',
      noSyncedFields: 'No sync fields selected — all values are edited locally.',
      fieldsSection: 'Fields driven from global record',
      fieldsHint: 'Checked fields become read-only locally and, when auto-sync is on, are updated from the global record.',
      disabledByOwner: 'disabled by owner',
      disabledByOwnerTitle: 'The global record owner does not allow sync',
      pullNow: 'Refresh now',
      pulling: 'Refreshing…',
      pullTitle: 'One-time pull of checked field values from the global record',
      pullDisabledTitle: 'The global record owner has restricted data access',
      unlink: 'Unlink',
      unlinking: 'Unlinking…',
      unlinkTitle: 'Unlink from global record and make the record fully local',
      errSaveSync: 'Could not save sync settings',
      errPull: 'Could not refresh from global record',
      errUnlink: 'Could not unlink',
      errAutoSyncDisallowed: 'The global record owner has not allowed sync — auto mode cannot be enabled.',
      cpFieldLabels: {
        display_name: 'Short name',
        legal_name: 'Legal name',
        inn: 'INN',
        logo: 'Logo',
      },
      llFieldLabels: {
        display_name: 'Short name',
        official_name: 'Full name',
        kind: 'Type',
        address: 'Address',
        description: 'Description',
        contact_phone: 'Contact phone',
        latitude: 'Latitude',
        longitude: 'Longitude',
        timezone: 'Timezone',
        photo: 'Photo',
        ride_scheme: 'Driving directions',
        operating_hours: 'Working hours',
      },
    },
  },
  planningPage: {
    common: {
      search: 'Search schedules and counterparties',
      total: 'Total',
      loading: 'Loading…',
      detailLoading: 'Loading details…',
      selectHint: 'Pick a schedule on the left to see details.',
      prevWeek: 'Previous week',
      nextWeek: 'Next week',
      prevMonth: 'Previous month',
      nextMonth: 'Next month',
      today: 'Today',
      disabled: 'Disabled',
      scheduleDisabledTitle: 'Schedule is disabled',
    },
    create: {
      button: 'Schedule',
      titleInbound: 'New supply schedule',
      titleOutbound: 'New shipping schedule',
      name: 'Name',
      namePlaceholder: 'e.g. Supply · Partner (warehouse)',
      targetLocation: 'By location',
      targetDirection: 'By direction',
      partnerLocation: 'Partner location',
      direction: 'Direction',
      choosePlaceholder: 'Choose…',
      km: 'km',
      min: 'min',
      descriptionLabel: 'Description',
      submit: 'Save',
      created: 'Schedule created',
      saved: 'Schedule saved',
      deleted: 'Schedule deleted',
      delete: 'Delete schedule',
      editTitle: 'Edit schedule',
      edit: 'Edit schedule',
      error: 'Failed to save the schedule',
    },
    point: {
      addButton: 'Point',
      createTitle: 'New schedule point',
      editTitle: 'Schedule point',
      overrideTitle: 'Series exception',
      date: 'Date',
      unit: 'Unit',
      notes: 'Notes',
      activeTitle: 'Active',
      activeDesc: 'Inactive points are excluded from planning',
      delete: 'Delete point',
      created: 'Point added',
      saved: 'Point saved',
      deleted: 'Point deleted',
      skipped: 'Point skipped for this date',
      skipThisDay: 'Skip only this date',
      skipThisDayHint: 'Creates a series exception: this date will have no point.',
      convertToWeekly: 'Make weekly',
      convertToWeeklyTitle: 'Convert to weekly series',
      convertToWeeklyHint: 'Current date becomes the anchor; the point will repeat every week on this weekday.',
      convertedToWeekly: 'Point converted to weekly',
      convertBlockedByWeekly: 'This weekday already has a weekly series — you cannot create another one.',
      directionPointHint: 'This is a direction-schedule point — only the planned start time is needed. Service and maneuver time is summed from linked partner points.',
      startTimeSourceLabel: 'Start time',
      directionPickerLabel: 'Direction',
      noDirectionsWarn: 'No matching directions: partner location has no attached directions with an active schedule on this location.',
      restoreSeries: 'Restore to series',
      restoredFromOverride: 'Restored to series',
      overrideBadgeModified: 'Overridden',
      overrideBadgeSkipped: 'Skipped',
      scopeLabel: 'Apply changes to',
      scopeSeries: 'Series',
      scopeThisDay: 'Only {date}',
      scopeHintSeries: 'Changes apply to all weeks in the series.',
      scopeHintThisDay: 'Changes apply only to this date (an exception is created).',
      error: 'Failed to save the point',
      weeklyExistsHint: 'This day already has a weekly delivery — you can only add a one-off (it runs alongside the weekly one).',
      seriesNote: 'A weekly point repeats every week. Changes apply to the whole series.',
      maneuverPlaceholder: 'Auto (from queue)',
      maneuverHint: 'Empty — taken from the queue interval settings.',
      dayPointsTitle: 'Points of the selected day',
      cruiseCreationSourceLabel: 'Cruise creation source',
      daysAheadPlaceholder: 'N',
      daysAheadUnit: 'days',
      daysAheadShort: '{n} days ahead',
    },
    startTimeSource: {
      PLANNED_IN_TS: 'Planned in TS',
      PLANNED_IN_TS_hint: 'Initial value; can be moved while working with timeslots.',
      ASSIGNED: 'Pinned',
      ASSIGNED_hint: 'Point is locked to this time — cannot be moved in timeslots.',
      BY_DIRECTION: 'By direction',
      BY_DIRECTION_hint: 'Start time is inherited from a point of the selected direction schedule.',
    },
    serviceSource: {
      QUEUE_DEFAULT: 'From queue settings',
      QUEUE_DEFAULT_hint: 'Service time is taken from the queue schedule interval that contains the planned start time.',
      MANUAL: 'Specify explicitly',
      MANUAL_hint: 'Uses the value you enter (field below is required).',
      VOLUME_BASED: 'By cargo volume',
      VOLUME_BASED_hint: 'amount × "time per unit" from queue settings. Falls back to queue default if not configured.',
      STATISTICS: 'By cruise statistics',
      STATISTICS_hint: 'Average of the last 5 completed cruises of this schedule. Falls back to queue default if no data.',
      DEPENDENTS_SUM: 'Computed (from linked points)',
      DEPENDENTS_SUM_hint: 'Sum of service and maneuver minutes across all partner points linked to this direction on this date.',
    },
    cruiseCreationSource: {
      OPERATOR: 'Operator',
      OPERATOR_hint: 'An operator creates the cruise manually from the schedule point list.',
      INTEGRATION: 'Integration',
      INTEGRATION_hint: 'Cruise is pushed by an external system (counterparty request via integration).',
      BY_SCHEDULE: 'By schedule',
      BY_SCHEDULE_hint: 'Cruise is created automatically the specified number of calendar days before the point date.',
    },
    filters: {
      allCargoModes: 'All cargo types',
      allQueues: 'All queues',
      targetType: 'Target type',
      targetAll: 'All types',
      targetLocation: 'By location',
      targetDirection: 'By direction',
      activity: 'Activity',
      activityAll: 'All schedules',
      activityActive: 'Active only',
      activityInactive: 'Disabled only',
    },
    detail: {
      routeBlock: 'Route & parameters',
      weekTitle: 'Week schedule',
      weekHint: 'A dot means an active point. Click to see the day.',
      queue: 'Queue',
      priority: 'Priority',
      cargoMode: 'Cargo type',
      distance: 'Distance',
      transitTime: 'Transit time',
      inn: 'INN',
      noPointsDay: 'No planned points on this day.',
      typeWeekly: 'Weekly',
      typeOneOff: 'One-off',
      pointDisabled: 'disabled',
      amount: 'Amount',
      plannedStart: 'Planned start',
      serviceMinutes: 'Service',
      maneuverMinutes: 'Maneuver',
      serviceSource: 'Estimated service time',
      plannedStartTimeBlock: 'Planned start time',
      serviceTimeBlock: 'Service durations',
      expectedAmount: 'Expected amount',
      startTimeSourceShort: 'Source',
      cruiseLinkChip: 'Trip',
      cruiseLinkTitle: 'Linked to trip',
      dependentsCount: 'from {n} linked partner points',
      cruisesCountBlock: 'Approx. cruises',
    },
    supply: {
      listTitle: 'Delivery schedules',
      emptyTitle: 'No delivery schedules',
      emptyHint: 'Create your first schedule to plan inbound deliveries from counterparties.',
    },
    shipping: {
      listTitle: 'Shipping schedules',
      emptyTitle: 'No shipping schedules',
      emptyHint: 'Create your first schedule to plan outbound shipments to customers and directions.',
    },
    weekday: {
      mon: 'Mo', tue: 'Tu', wed: 'We', thu: 'Th', fri: 'Fr', sat: 'Sa', sun: 'Su',
    },
    weekStrip: {
      hasPlan: 'plan',
    },
    copy: {
      button: 'Copy schedule',
      title: 'Copy schedule to…',
      searchPlaceholder: 'Search by name or counterparty',
      directionFilterLabel: 'Direction',
      directionFilterAll: 'All directions',
      emptyList: 'No other schedules to copy to.',
      confirmWarn: 'Weekly points for the same weekdays in the selected schedules will be replaced. Linked OVERRIDE exceptions will be deleted.',
      submit: 'Copy to {n}',
      submitConfirm: 'Confirm replace',
      successToast: 'Copied to {targets} schedules: {copied} points, {overwritten} replaced',
      errorToast: 'Failed to copy schedule',
    },
    timeslots: {
      loadingQueues: 'Loading queues…',
      loadingBoard: 'Loading board…',
      noQueues: 'No queues',
      pickQueueTitle: 'Pick a queue',
      pickQueueHint: 'The current location has no queues. Add a queue in Configuration to plan timeslots.',
      noScheduleTitle: 'No schedule defined',
      noScheduleHint: 'The selected queue has no active schedule for this week. Create one in Configuration.',
      openQueuesCta: 'Open queues',
      slotFree: 'free',
      slotCapacityTitle: 'Occupied {occupied} of {capacity}',
      unallocatedTitle: 'Unallocated',
      unallocatedEmpty: 'All schedule points are placed into slots',
      unallocatedDragHint: 'Drag&drop into a slot — next iteration',
      dayNonWorking: 'Non-working day',
      dayNoTemplate: 'No active schedule',
      dayNoTemplateShort: 'No schedule',
      dayNoIntervals: 'No intervals',
      dayWeekend: 'Off',
      dayAllAllocated: 'Allocated',
      dayUnallocated: '{count} unalloc.',
      dayUnallocatedTitle: 'Unallocated points: {count}',
      directionInbound: 'Inbound',
      directionOutbound: 'Outbound',
      queueType: {
        service: 'Service',
        parking: 'Parking',
        control: 'Control',
      },
      summary: {
        totalSlots: 'total slots',
        empty: 'free',
        occupied: 'occupied',
        full: 'full',
        over: 'overbook',
        unallocated: 'unallocated',
      },
      detailTitle: 'Slot details',
      detailEmptyTitle: 'No slot selected',
      detailEmptyHint: 'Click a slot on the grid to see details.',
      detail: {
        occupiedLabel: 'occupied',
        occupancyPctLabel: 'load',
        capacityLabel: 'capacity',
        overbookWarn: 'Slot is overbooked — more points than spots.',
        paramsLabel: 'Slot parameters',
        pointsLabel: 'Schedules',
        pointsEmpty: 'Nothing planned in this slot yet.',
        params: {
          active_docks: 'Active docks',
          maneuver_time_sec: 'Maneuver',
          service_time_sec: 'Service',
          parking_spots: 'Parking spots',
          average_stay_sec: 'Average stay',
          inspectors: 'Inspectors',
          inspection_time_sec: 'Inspection time',
        },
        category: {
          inbound: 'Inbound',
          inbound_hint: 'Inbound (supply) schedule from a partner.',
          outboundDirection: 'Direction',
          outboundDirection_hint: 'Outbound schedule by direction — a consolidated cruise covering several partners.',
          outboundLocation: 'Outbound',
          outboundLocation_hint: 'Outbound schedule for a specific partner (time set on this point, not inherited from a direction).',
        },
        loadShort: 'Svc {service} · Mnv {maneuver} min',
        byCategoryTitle: 'Time by category',
        byCategory: {
          serviceInbound: 'Inbound',
          serviceDirection: 'Direction',
          serviceOutbound: 'Outbound',
          maneuverTotal: 'Maneuver (total)',
        },
      },
      moveError: 'Failed to move point',
    },
  },
  /* __I18N_ANCHOR_CONFIGURATION__ */
  configurationPage: {
    myData: {
      loading: 'Loading…',
      loadError: 'Could not load location data.',
      objectSection: 'Location',
      fieldType: 'Type',
      fieldTimezone: 'Timezone',
      fieldOrg: 'Organisation',
      statsUsers: 'users:',
      statsVehicles: 'vehicles:',
      statsDevices: 'devices:',
      usersSection: 'Location users',
      noUsers: 'No users with explicit access to this location.',
      accessOwner: 'owner',
      accessExplicit: 'explicit',
      accessInherited: 'inherited',
      vehiclesSection: 'Vehicles',
      noVehicles: 'No vehicles assigned to this location.',
      hasTrailer: 'trailer',
      vehicleActive: 'active',
      vehicleOff: 'off',
      devicesSection: 'Devices',
      noDevices: 'No shared devices assigned to this location yet.',
      devicesPlaceholder: 'System → Devices section is under development',
      fieldCarrier: 'Carrier',
      fieldDriver: 'Driver',
      fieldBrand: 'Brand',
      fieldCapacity: 'Capacity',
      fieldTrailer: 'Trailer',
      yes: 'yes',
      no: 'no',
      vehiclesEditHint: 'Full editing will be available in System → Vehicles',
      lastActivity: 'Last activity',
      devicesEditHint: 'Full editing will be available in System → Devices',
      openInUsers: 'Open in System → Users',
      ownerFullAccess: 'Account owner has full access to all locations by default.',
      explicitGrant: 'Explicit LocationAccess grant for this location.',
      inheritedGrant: 'Access inherited from CompanyAccess (no explicit LocationAccess restriction).',
      readOnly: 'read-only',
      readWrite: 'read + write',
    },
    directions: {
      title: 'Directions',
      empty: 'No directions. Create one.',
      createButton: 'New Direction',
      createModal: {
        title: 'Create Direction',
        createButton: 'Create',
      },
      fields: {
        name: 'Name',
        code: 'Code',
        description: 'Description',
        active: 'Active',
        params: 'Parameters',
        avgDistanceKm: 'Average distance to location, km',
        avgTravelTimeMin: 'Average travel time to location, min',
        avgInternalDistanceKm: 'Average distance between locations, km',
        avgInternalTravelTimeMin: 'Average travel time between locations, min',
      },
      members: {
        title: 'Direction Members',
        empty: 'No members. Add counterparty locations.',
        addButton: 'Add Member',
        searchPlaceholder: 'Search by counterparty or address',
      },
      deleteConfirm: 'Delete direction «{{name}}»?',
    },
  },
  monitoring: {
    cargo: {
      title: 'Cargo',
      shown: 'Showing {n} of {total}',
      loadMore: 'Load more',
      loadingMore: 'Loading…',
      searchPlaceholder: 'Search: document, location, partner, GUID',
      empty: 'No cargo records. Run `python manage.py seed_demo_cargo` for demo data.',
      bailorLabel: 'Bailor',
      filter: {
        all: 'All',
        noCruise: 'No trip',
        openCruise: 'With trip',
      },
      col: {
        document: 'Document',
        partners: 'Sender / Receiver',
        mode: 'Mode / qty',
        operation: 'Operation / plan',
        cruise: 'Trip',
        created: 'Created',
      },
    },
    transport: {
      searchPlaceholder: 'Search: vehicle, driver, trip, GUID',
      countSuffix: 'Total: {n}',
      columnEmpty: 'Empty',
      typeFilter: {
        label: 'Vehicle type',
        all: 'All types',
      },
      queueFilter: {
        label: 'Service queue',
        all: 'All queues',
      },
      vehicleType: {
        car: 'Car',
        trailer: 'Trailer',
        railcar: 'Railcar',
        airplane: 'Airplane',
        ship: 'Ship',
      },
      contact: {
        terminal: 'Reachable via driver app',
        phone: 'Reachable via phone bot',
      },
      columns: {
        expected: 'Expected',
        waiting: 'Waiting',
        serving: 'In service',
        leaving: 'Leaving',
      },
      time: {
        plan: 'planned',
        arrived: 'arrived',
        begun: 'started',
        finished: 'finished',
      },
    },
    processing: {
      searchPlaceholder: 'Search: trip, vehicle, driver, GUID',
      filterAll: 'All',
      shown: 'showing {n} of {total}',
      loadMore: 'Load more',
      loadingMore: 'Loading…',
      structureLabel: 'Location structure',
      structureAll: 'Whole location',
      statusFilter: {
        label: 'Trip status',
        active: 'In progress',
        done: 'Completed',
        cancelled: 'Cancelled',
        all: 'All',
      },
      gates: {
        scrollLeft: 'Scroll gates left',
        scrollRight: 'Scroll gates right',
        free: 'Free',
        inactive: 'Inactive',
        emptyAll: 'No loading points.',
        emptyFiltered: 'No loading points in the selected queue.',
        openCruise: 'Open trip',
        byDriverTerminal: 'Driver terminal',
      },
      planned: {
        empty: 'No trip points in this location.',
        col: {
          name: 'Name · Operation · Status',
          cargo: 'Cargo',
          priority: 'Queue · Service place',
          transport: 'Vehicle',
          plan: 'Plan',
          fact: 'Fact',
        },
        overflow: 'No space',
        overflowHint: 'No free gates or parking spots — the vehicle is waiting at the entrance.',
      },
      panel: {
        closeAll: 'Close panel',
        cp: {
          title: 'Trip point',
          sectionTrip: 'Trip',
          tabCargo: 'Cargo',
          tabRoute: 'Route',
          cargos: 'Cargo',
          tasks: 'Tasks',
          fields: {
            cruise: 'Trip',
            vehicle: 'Vehicle',
            driver: 'Driver',
            mode: 'Mode',
            site: 'Site',
            plan: 'Plan',
            begin: 'Started',
            finish: 'Finished',
          },
          sectionCargo: 'Cargo',
          sectionRoute: 'Route',
          sectionCruise: 'Trip',
          f: {
            name: 'Point name',
            order: 'Route #',
            status: 'Status',
            location: 'Service place',
            parking: 'Parking spot',
            operation: 'Operation',
            priority: 'Priority',
            queue: 'Queue',
            vehicle: 'Vehicle no.',
            driver: 'Driver',
            boundLocation: 'Bound location',
            from: 'From',
            to: 'To',
            planBegin: 'Planned start',
            factArrival: 'Actual arrival',
            factBegin: 'Actual start',
            factFinish: 'Actual finish',
          },
          cargo: {
            empty: 'No cargo for this point.',
            which: 'Cargo',
            amount: 'Amount',
            from: 'From',
            to: 'To',
            queue: 'Queue',
          },
          route: {
            empty: 'No points in this trip.',
            load: 'Loading',
            unload: 'Unloading',
            current: 'Current',
            open: 'Open trip point',
          },
          cruise: {
            empty: 'No points in this trip.',
            current: 'Current',
            open: 'Open trip point',
            gate: 'Gate',
            parking: 'Parking',
            queue: 'Queue',
            vehicle: 'Vehicle',
            driver: 'Driver',
            cargo: 'Cargo',
            plan: 'Plan',
            arrival: 'Arrival',
            begin: 'Started',
            finish: 'Finished',
            noTimes: 'Service not started yet',
          },
          transition: {
            title: 'Change status',
            none: 'No available transitions from the current status.',
            ok: 'Status: {status}.',
            err: {
              default: 'Failed to change status.',
              not_in_matrix: 'Transition is not allowed from this status.',
              terminal_status: 'Entity is in a terminal status — no transitions available.',
              gate_occupied: 'Gate is already occupied.',
              no_gate_sent_to_parking: 'No free gate in the service queue — the vehicle was sent to parking.',
              no_gate_wait: 'No free gate available — please wait.',
              no_gate_no_parking: 'No free gates or parking spots — the vehicle is waiting at the entrance.',
              no_parking_spot: 'No free parking spots.',
              required_for_info: 'Vehicle number is required for the active status.',
              executor_required: 'Assign an executor to the task.',
              reason_required: 'A reason is required for this status.',
              reason_mismatch: 'The reason does not match the target status.',
              reschedule_required: 'Specify a cruise to reschedule to.',
              out_of_scope: 'Status is not applicable to this entity.',
              status_not_seeded: 'Status is missing from the catalog.',
            },
          },
          reason: {
            title: 'Pick a reason · {status}',
            empty: 'No reasons available for this status.',
            apply: 'Apply',
          },
          assign: {
            title: 'Vehicle assignment',
            none: 'No vehicle assigned',
            assignBtn: 'Assign vehicle',
            changeBtn: 'Change',
            removeBtn: 'Remove',
            searchPlaceholder: 'Search by plate or brand…',
            empty: 'No vehicles found.',
            createNew: 'New vehicle',
            lockedInService: 'Cruise is in service — the vehicle cannot be changed.',
            notifiedYes: 'Notification sent to the driver terminal.',
            notifiedAlready: 'The driver has already been notified about this cruise.',
            notifiedNo: 'No active driver terminal found — notification not sent.',
            ok: 'Vehicle assigned.',
            removed: 'Vehicle removed.',
            err: 'Could not complete the action.',
          },
          worker: {
            title: 'Assign to worker',
            assignBtn: 'Assign',
            pick: 'Select a worker…',
            noWorkers: 'No eligible workers for this profile.',
            ok: 'Task assigned.',
            err: 'Could not assign the task.',
            notifiedYes: 'Notification sent to the worker’s terminal.',
            notifiedAlready: 'The worker has already been notified about this task.',
            notifiedNo: 'The worker is off shift — notification not sent.',
          },
          truckModal: {
            title: 'New vehicle',
            number: 'Plate number',
            numberPlaceholder: 'А123ВС77',
            brand: 'Brand',
            vehicleType: 'Vehicle type',
            model: 'Model',
            modelPlaceholder: 'KAMAZ-6520',
            create: 'Create',
            err: 'Could not create the vehicle.',
          },
        },
        task: {
          title: 'Task',
          mainTaskBadge: 'Main',
          fields: {
            type: 'Type',
            site: 'Site',
            cruise: 'Trip',
            created: 'Created',
            assigned: 'Assigned',
            begin: 'Begin',
            finished: 'Finished',
            user: 'Assignee',
            author: 'Author',
            source: 'Source',
          },
        },
      },
    },
  },
}

export default en
